import React from 'react'
import {
  Card,
  Badge,
  Modal,
  Descriptions,
  Flex,
  Button,
  Tabs,
  ConfigProvider,
} from 'antd'
import { FormattedMessage } from 'react-intl'
import { useMeasure } from 'react-use'
import ReactPlayer from 'react-player'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import { ToolOutlined } from '@ant-design/icons'

// Steps arrive in the order they should be shown: the Wiretronics provider sorts
// tools into service order, and Concept-T returns documents in the templates
// table's sort_order. The component renders what it is given.

// A connector may use the same tool for several steps, so the part number is
// not unique across a pin's tools. Each step carries the id of its used_with
// edge (unique per step); fall back to a composite key for older payloads.
const stepKey = (step, index) =>
  step.id != null
    ? String(step.id)
    : `${step.tool?.part_number ?? ''}::${step.tool?.used_for ?? ''}::${index}`

// A tool is a thumbnail beside a short video; a document is a repair procedure
// with photographs and two-column tables. They do not fit in the same box, so
// each tab takes its height from its own step, and the modal its width from the
// widest thing it holds. Providers coexist -- on zoox a connector shows
// Wiretronics tools beside Concept-T documents in one modal -- so a tool tab
// keeps its own short pane rather than stretching to a document's. A modal of
// tools alone keeps the dimensions it has always had.
const TOOL_MODAL_WIDTH = '800px'
const DOCUMENT_MODAL_WIDTH = '1100px'
const TOOL_PANE_HEIGHT = '300px'
const DOCUMENT_PANE_HEIGHT = '60vh'

const paneHeightFor = (step) =>
  step.document ? DOCUMENT_PANE_HEIGHT : TOOL_PANE_HEIGHT

// A step is either a tool (Wiretronics) or a document (Concept-T), never both.
const tabLabel = (step) =>
  step.document ? (
    step.document.title
  ) : (
    <>
      <p>
        {step.tool.part_number}
        {step.tool.cavities_display && ` (${step.tool.cavities_display})`}
      </p>
      {step.tool.used_for}
    </>
  )

const RelatedContentButton = ({
  tools: steps,
  pin,
  isLoading,
  onBeforeOpen,
  buttonTheme,
}) => {
  const [open, setOpen] = React.useState(false)

  const hasDocuments = steps.some((step) => step.document)

  const paneStyle = {
    width: '50%',
    boxSizing: 'border-box',
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }

  // A document is prose, not a centred image: it reads from the top and has to
  // scroll, where a fixed pane would otherwise clip it.
  const documentPaneStyle = {
    ...paneStyle,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '12px 16px',
    textAlign: 'left',
  }

  const items = steps.map((step, index) => {
    const video = step.instruction
    // Tools keep both panes even with no video, exactly as before. A document
    // without one takes the full width instead of sitting beside an empty box --
    // most Concept-T content has no video at all.
    const showVideoPane = video || !step.document
    const height = paneHeightFor(step)

    return {
      key: stepKey(step, index),
      label: tabLabel(step),
      children: (
        <Flex gap="middle" style={{ height }} data-testid="step-pane">
          {step.document ? (
            <Card.Grid
              style={{
                ...documentPaneStyle,
                height,
                width: showVideoPane ? '50%' : '100%',
              }}
              hoverable={false}
            >
              <DocumentBody document={step.document} />
            </Card.Grid>
          ) : (
            <Card.Grid style={{ ...paneStyle, height }} hoverable={false}>
              <div>
                <p>{step.tool.part_number}</p>
              </div>
              <ToolImage tool={step} key={step.tool.part_number} />
            </Card.Grid>
          )}
          {showVideoPane && (
            <Card.Grid style={{ ...paneStyle, height }} hoverable={false}>
              <div>
                <p>
                  {step.document ? step.document.title : step.tool.used_for}
                </p>
              </div>
              {video ? (
                <ReactPlayer
                  key={video.url_high_quality}
                  src={video.url_high_quality}
                  controls={true}
                  height="100%"
                />
              ) : (
                <div>No video available</div>
              )}
            </Card.Grid>
          )}
        </Flex>
      ),
    }
  })

  const pinDescription = []

  // The identifier above the tabs is a pin only in the pinout. Everywhere else it
  // names whatever the content belongs to -- a connector, a harness, a DTC -- so
  // the caller passes a message id and generic.pin is the default the pinout
  // relies on.
  if (pin?.pin != null) {
    pinDescription.push({
      label: <FormattedMessage id={pin.label ?? 'generic.pin'} />,
      children: pin.pin,
    })
  }

  if (pin?.description) {
    pinDescription.push({
      label: <FormattedMessage id="generic.description" />,
      children: pin.description,
    })
  }

  const trigger = (
    <Badge count={steps.length} size="small" color="green">
      <Button
        disabled={steps.length === 0}
        type="default"
        icon={<ToolOutlined />}
        onClick={() => {
          // The harness view tooltip is a tippy instance outside the modal's
          // stacking context, so it would otherwise sit on top of the modal it
          // just opened. Callers that live inside one dismiss it here, the way
          // the Related Diagrams button and StateButtons beside it do.
          onBeforeOpen && onBeforeOpen()
          setOpen(true)
        }}
        loading={isLoading}
        data-testid="related-content-button"
      />
    </Badge>
  )

  return (
    <>
      {/* buttonTheme covers the trigger and nothing else. The harness view needs
          the light algorithm for a button sitting on a white tooltip card, but
          the modal opens over the app itself and must stay on the app's theme.
          Wrapping both -- which is what the caller used to do -- gave the modal
          GUNMETAL header and content backgrounds inherited from the root's
          component tokens, with near-black text from the forced light algorithm
          on top of them. */}
      {buttonTheme ? (
        <ConfigProvider theme={buttonTheme}>{trigger}</ConfigProvider>
      ) : (
        trigger
      )}
      <Modal
        title={hasDocuments ? 'Related Content' : 'Tools'}
        open={open}
        onOk={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        width={hasDocuments ? DOCUMENT_MODAL_WIDTH : TOOL_MODAL_WIDTH}
        // Tools are a fixed-size pair of panes. A document sets its own height
        // from the content, so pinning one here would just clip it.
        style={hasDocuments ? undefined : { height: TOOL_PANE_HEIGHT }}
        // Above the app's own overlays: the DTC list renders a memo popover at
        // 8000 and a memo tooltip at 9999, either of which would otherwise cover
        // a modal opened from the same row. antd's default is 1000.
        zIndex={10000}
        footer={null}
      >
        {pinDescription.length > 0 && (
          <>
            <Descriptions items={pinDescription} />
            <br />
          </>
        )}
        <Card
          styles={{
            body: {
              padding: 0,
              overflow: 'hidden',
            },
          }}
        >
          <Tabs
            items={items}
            size="large"
            tabBarStyle={{ padding: '0 24px', marginBottom: 12 }}
          />
        </Card>
      </Modal>
    </>
  )
}

// html arrives sanitized from the provider -- nh3 with an explicit tag and
// attribute allowlist, applied before anything is cached, so Redis never holds
// markup that has not been through it.
const DocumentBody = ({ document }) => {
  if (document.html) {
    return (
      <div
        className="related-content-document"
        data-testid="document-html"
        dangerouslySetInnerHTML={{ __html: document.html }}
      />
    )
  }

  return <div data-testid="document-text">{document.description}</div>
}

const ToolImage = ({ tool, scaleUp = true }) => {
  const [containerRef, { width: containerWidth, height: containerHeight }] =
    useMeasure()
  const [imageNaturalWidth, setImageNaturalWidth] = React.useState(0)
  const [imageNaturalHeight, setImageNaturalHeight] = React.useState(0)
  const transformRef = React.useRef(null)
  const imageScale = React.useMemo(() => {
    if (
      containerWidth === 0 ||
      containerHeight === 0 ||
      imageNaturalWidth === 0 ||
      imageNaturalHeight === 0
    )
      return 0
    const scale = Math.min(
      containerWidth / imageNaturalWidth,
      containerHeight / imageNaturalHeight,
    )
    return scaleUp ? scale : Math.max(scale, 1)
  }, [
    scaleUp,
    containerWidth,
    containerHeight,
    imageNaturalWidth,
    imageNaturalHeight,
  ])

  React.useEffect(() => {
    const image = new Image()
    image.onload = () => handleImageOnLoad(image)
    image.src = tool.tool.thumbnail
  }, [tool.tool.thumbnail])

  const handleImageOnLoad = (image) => {
    setImageNaturalWidth(image.naturalWidth)
    setImageNaturalHeight(image.naturalHeight)
  }

  // Center and scale image when dimensions change
  React.useEffect(() => {
    if (transformRef.current && imageScale > 0) {
      transformRef.current.centerView(imageScale, 0)
    }
  }, [imageScale])

  const zoomFactor = 5

  return (
    <div
      ref={containerRef}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {imageScale > 0 && (
        <TransformWrapper
          ref={transformRef}
          limitToBounds={true}
          initialScale={imageScale}
          minScale={imageScale}
          maxScale={imageScale * zoomFactor}
          centerOnInit
        >
          <TransformComponent
            wrapperStyle={{
              height: '100%',
              width: '100%',
            }}
          >
            <img
              src={tool.tool.thumbnail}
              alt={tool.tool.part_number}
              data-testid="tool-thumbnail"
            />
          </TransformComponent>
        </TransformWrapper>
      )}
    </div>
  )
}

// ToolsButton is kept as an alias so the pinout call sites and the three test
// files that mock it need no change; the component handles documents too now.
export { RelatedContentButton, RelatedContentButton as ToolsButton }
